import React, { useEffect, useState } from 'react';
import { HomeContainer, HeroSection, ContentSection, Card, CTAButton, StatsSection, StatItem, NextGameSection } from './Home.Styles';
import { Fixture } from '../../models/Fixture';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { entriesUrl, CACHE_KEYS } from '../../utils/apiEndpoints';
import TeamLogo from '../../components/TeamLogo';

const NextGame: React.FC = () => {
  const [nextGame, setNextGame] = useState<Fixture | null>(null);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const fetchNextGame = async () => {
      try {
        const now = new Date();

        const items = await fetchItemsWithCache<Fixture>(
          entriesUrl('tigers_fixtures'),
          CACHE_KEYS.fixtures,
          5 * 60 * 1000
        );

        // Find the next game that hasn't happened yet
        const nextFixtures = items
          .filter((fixture) => new Date(`${fixture.date}T${fixture.time}`) > now)
          .sort((a, b) => {
            const dateA = new Date(`${a.date}T${a.time}`);
            const dateB = new Date(`${b.date}T${b.time}`);
            return dateA.getTime() - dateB.getTime();
          });

        setNextGame(nextFixtures[0] ?? null);
      } catch (error) {
        console.error('Error fetching next game:', error);
      }
    };

    fetchNextGame();
  }, []);

  useEffect(() => {
    if (!nextGame) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const gameTime = new Date(`${nextGame.date}T${nextGame.time}`).getTime();
      const distance = gameTime - now;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(timer);
  }, [nextGame]);

  if (!nextGame) return null;

  const isHome = nextGame.homeTeam === 'Sheffield Ice Tigers';
  const opponent = isHome ? nextGame.awayTeam : nextGame.homeTeam;

  return (
    <NextGameSection>
      <h2>Our Next Game</h2>
      <div className="game-details">
        <div className="teams-container">
          <div className="team">
            <TeamLogo team={isHome ? 'Sheffield Ice Tigers' : opponent} width="50px" height="50px" />
            <span className="name">{isHome ? 'Sheffield Ice Tigers' : opponent}</span>
          </div>
          <span className="versus">vs</span>
          <div className="team">
            <TeamLogo team={isHome ? opponent : 'Sheffield Ice Tigers'} width="50px" height="50px" />
            <span className="name">{isHome ? opponent : 'Sheffield Ice Tigers'}</span>
          </div>
        </div>
        <div className="location-container">
          <span className="at-symbol">@</span>
          <span className="venue">{nextGame.venue}</span>
        </div>
      </div>
      <div className="countdown">
        <div className="countdown-item">
          <div className="value">{countdown.days}</div>
          <div className="label">Days</div>
        </div>
        <div className="countdown-item">
          <div className="value">{countdown.hours}</div>
          <div className="label">Hours</div>
        </div>
        <div className="countdown-item">
          <div className="value">{countdown.minutes}</div>
          <div className="label">Mins</div>
        </div>
        <div className="countdown-item">
          <div className="value">{countdown.seconds}</div>
          <div className="label">Secs</div>
        </div>
      </div>
    </NextGameSection>
  );
};

const Home: React.FC = () => {
  return (
    <HomeContainer>
      <HeroSection>
        <h1>Welcome to Sheffield Ice Tigers</h1>
        <NextGame />
      </HeroSection>
      
      <ContentSection>
        <Card>
          <h2>Upcoming Games</h2>
          <p>Check out our upcoming fixtures and join us for the next exciting match!</p>
          <CTAButton to="/fixtures">View Schedule</CTAButton>
        </Card>
        <Card>
          <h2>Latest Results</h2>
          <p>See how we performed in our recent games and track our progress.</p>
          <CTAButton to="/results">View Results</CTAButton>
        </Card>
        <Card>
          <h2>Team Stats</h2>
          <p>Explore our team and player statistics from the current season.</p>
          <CTAButton to="/player-stats">View Stats</CTAButton>
        </Card>
      </ContentSection>

      <StatsSection>
        <StatItem>
          <div className="value">20</div>
          <div className="label">Years of Excellence</div>
        </StatItem>
        <StatItem>
          <div className="value">100+</div>
          <div className="label">Games Played</div>
        </StatItem>
        <StatItem>
          <div className="value">63</div>
          <div className="label">Team Members</div>
        </StatItem>
      </StatsSection>
    </HomeContainer>
  );
};

export default Home; 